/**
 * NxListViewer v2.0 -- Reusable, versionable list/table viewer with split detail.
 * Shared component callable from any artifact.
 * v2.0: Split view (table left, detail right), active row highlight, filter input.
 *
 * @module presentation/shared/NxListViewer
 * @version 2.0
 */

export const NX_LIST_VIEWER_VERSION = '2.0';

// Deuda 4: el escape local solo cambiaba "<" y este archivo lo usa dentro de atributos con comilla doble.
import { esc as escHtml } from './texto.js';

/**
 * Render a data list with configurable columns.
 * v2: Wraps in split layout if detailRenderer is provided.
 *
 * @param {Object} config
 * @param {string} config.id - Unique ID
 * @param {Array<Object>} config.columns - [{key, label, width?, render?}]
 * @param {Array<Object>} config.data - Data rows
 * @param {string} [config.sortBy]
 * @param {string} [config.sortDir='asc']
 * @param {string} [config.emptyMessage]
 * @param {string} [config.filterPlaceholder] - v2: filter input placeholder
 * @returns {string} HTML string
 */
export function renderNxList(config) {
  const { id, columns, data, sortBy, sortDir = 'asc', emptyMessage = 'Sin datos', filterPlaceholder } = config;

  let sorted = [...data];
  if (sortBy) {
    sorted.sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }

  const filterHtml = filterPlaceholder
    ? `<input type="text" class="nx-list-filter form-input" data-nx-filter="${id}" placeholder="${escHtml(filterPlaceholder)}" style="font-size:0.875rem;padding:4px 8px;margin-bottom:6px;width:100%;max-width:300px">`
    : '';

  if (sorted.length === 0) {
    return `<div class="nx-list-viewer" id="${id}" data-nx-list-version="${NX_LIST_VIEWER_VERSION}">
      ${filterHtml}
      <p class="empty-state">${escHtml(emptyMessage)}</p>
    </div>`;
  }

  const headerCells = columns.map(col =>
    `<th class="nx-list-th" data-sort-key="${col.key}" style="${col.width ? `width:${col.width}` : ''}">${escHtml(col.label)} <span class="nx-sort-icon">${sortBy === col.key ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}</span></th>`
  ).join('');

  const rows = sorted.map((row, idx) => {
    const cells = columns.map(col => {
      const value = col.render ? col.render(row[col.key], row) : escHtml(String(row[col.key] ?? ''));
      return `<td class="nx-list-td">${value}</td>`;
    }).join('');
    return `<tr class="nx-list-row" data-nx-row-idx="${idx}">${cells}</tr>`;
  }).join('');

  return `<div class="nx-list-viewer" id="${id}" data-nx-list-version="${NX_LIST_VIEWER_VERSION}">
    ${filterHtml}
    <div class="nx-list-split">
      <div class="nx-list-table-wrap">
        <table class="nx-list-table">
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div class="nx-list-detail" id="${id}-detail" hidden></div>
    </div>
  </div>`;
}

/**
 * Bind sort + click + filter events.
 * v2: Supports detailRenderer for split view, filter input.
 *
 * @param {HTMLElement} container
 * @param {string} listId
 * @param {Object} config
 * @param {Function} [onRowClick] - (rowData) => void (for legacy)
 * @param {Function} [detailRenderer] - (rowData, detailEl) => void (v2: renders in split panel)
 */
export function bindNxListEvents(container, listId, config, onRowClick, detailRenderer) {
  const list = container.querySelector(`#${listId}`);
  if (!list) return;

  // Filter input
  const filterInput = list.querySelector(`[data-nx-filter="${listId}"]`);
  if (filterInput) {
    filterInput.addEventListener('input', () => {
      const term = filterInput.value.toLowerCase();
      const rows = list.querySelectorAll('.nx-list-row');
      rows.forEach(row => {
        const text = row.textContent.toLowerCase();
        row.style.display = text.includes(term) ? '' : 'none';
      });
    });
  }

  list.addEventListener('click', (e) => {
    // Sort
    const th = e.target.closest('.nx-list-th');
    if (th) {
      const key = th.dataset.sortKey;
      if (config.sortBy === key) {
        config.sortDir = config.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        config.sortBy = key;
        config.sortDir = 'asc';
      }
      list.outerHTML = renderNxList(config);
      bindNxListEvents(container, listId, config, onRowClick, detailRenderer);
      return;
    }

    // Row click
    const row = e.target.closest('.nx-list-row');
    if (row) {
      const idx = parseInt(row.dataset.nxRowIdx, 10);
      const sorted = [...config.data];
      if (config.sortBy) {
        sorted.sort((a, b) => {
          const va = a[config.sortBy] ?? '';
          const vb = b[config.sortBy] ?? '';
          const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
          return config.sortDir === 'desc' ? -cmp : cmp;
        });
      }
      const item = sorted[idx];
      if (!item) return;

      // Highlight active row
      list.querySelectorAll('.nx-list-row').forEach(r => r.classList.remove('nx-list-row--active'));
      row.classList.add('nx-list-row--active');

      // v2: split detail panel
      if (detailRenderer) {
        const detailEl = container.querySelector(`#${listId}-detail`);
        if (detailEl) {
          detailEl.hidden = false;
          detailRenderer(item, detailEl);
        }
      }

      if (onRowClick) onRowClick(item);
    }
  });
}
